import type { SeatCardMode } from './nineSeatTableDemo';

/**
 * The /demo-table harness's multi-run vote scenarios (EN-10856): every state
 * the vote can put a table in, reachable in one click, with the fields the
 * live table gets in each — `runs`, the initiator, each seat's vote token,
 * the hand number and the clock. Pure, so the scenarios are testable on their
 * own; DemoTable dispatches the result into the real store.
 *
 * Three seats are in the hand: YOU (the POV), the OTHER all-in and a THIRD
 * caller. Everyone else has folded, which is what makes the felt state's
 * dimming visible.
 */
export type VoteStep =
  | 'off'
  | 'you-choose' // you went all in first: you choose 1X / 2X / 3X
  | 'you-offered' // you offered: the others are asked
  | 'they-choose' // they went all in first: they are deciding
  | 'they-offered' // they offered: you answer 1X or the offer
  | 'you-agreed' // you agreed: waiting on the others
  | 'you-folded' // you folded earlier: watching the vote
  | 'declined' // it ran once — RUN IT 1X
  | 'agreed'; // unanimous — the boards run out

export type VoteCast = { you: number; other: number; third: number };

export type VoteOffer = 2 | 3;

/**
 * The longest window a table template allows (multi_run_vote_secs caps at 60;
 * 0 means the 8s default), as the 9/17 demo table used, so there is time to
 * talk over it. Every step restarts it.
 */
export const DEMO_VOTE_SECS = 60;

/**
 * A fresh hand number per step shown. The reducer's vote latch holds each
 * seat's bubble for the rest of a hand (EN-663 AC-5.3), so steps sharing one
 * would carry a later step's bubbles back into an earlier one. Every step
 * spells out its own tokens, so none of them needs the latch's memory.
 */
export const demoVoteHand = (step: number): string => `demo-vote-${step}`;

const OFFER_TOKEN: Record<VoteOffer, string> = { 2: 'Run it Twice?', 3: 'Run it Thrice?' };
const DECLINE_TOKEN = 'No';

/** The two opponents in the hand: the first two occupied seats after yours. */
export const castVote = (occupiedIds: number[], you: number): VoteCast | null => {
  const others = occupiedIds.filter((id) => id !== you);
  if (!occupiedIds.includes(you) || others.length < 2) return null;
  return { you, other: others[0], third: others[1] };
};

/** Folds every seat outside the hand (and yours, while you watch folded). */
export const voteSeatModes = (
  occupiedIds: number[],
  cast: VoteCast,
  step: VoteStep,
  base: Record<number, SeatCardMode>
): Record<number, SeatCardMode> => {
  const inHand = new Set([cast.other, cast.third]);
  if (step !== 'you-folded') inHand.add(cast.you);
  const modes: Record<number, SeatCardMode> = { ...base };
  for (const id of occupiedIds) {
    if (!inHand.has(id)) modes[id] = 'fold';
    else if (modes[id] === 'fold' || modes[id] === 'none') delete modes[id];
  }
  return modes;
};

/**
 * How a vote that ran once ended: you went first and picked 1X, you declined
 * their offer, or the third seat declined it.
 */
export type DeclineCase = 'you-picked-1x' | 'you-declined' | 'third-declined';

/** Is the step the run-out (boards dealt) rather than the vote window? */
export const isRunOutStep = (step: VoteStep): boolean => step === 'agreed';

type VoteFrame = { game: Record<string, any> };

/** The frame for a step, on top of one DemoTable built for the same seats. */
export const applyVoteStep = <F extends VoteFrame>(
  frame: F,
  step: VoteStep,
  {
    cast,
    offer,
    startedAt,
    hand,
    declined = 'third-declined'
  }: {
    cast: VoteCast;
    offer: VoteOffer;
    startedAt: number;
    /** Which step this is, for the hand number (demoVoteHand). */
    hand: number;
    declined?: DeclineCase;
  }
): F => {
  if (step === 'off') return frame;

  const youInitiate =
    step === 'you-choose' ||
    step === 'you-offered' ||
    (step === 'declined' && declined === 'you-picked-1x');
  const initiator = youInitiate ? cast.you : cast.other;
  const token = OFFER_TOKEN[offer];

  const tokens: Record<number, string> = {};
  let runs: string;
  switch (step) {
    case 'you-choose':
    case 'they-choose':
      runs = 'v0/0/0/0';
      break;
    case 'you-offered':
      tokens[cast.you] = token;
      runs = `v${cast.you}/${offer}/1/0`;
      break;
    case 'they-offered':
    case 'you-folded':
      tokens[cast.other] = token;
      runs = `v${cast.other}/${offer}/1/0`;
      break;
    case 'you-agreed':
      tokens[cast.other] = token;
      tokens[cast.you] = token;
      runs = `v${cast.other}.${cast.you}/${offer}/2/0`;
      break;
    case 'declined':
      if (declined === 'you-picked-1x') {
        tokens[cast.you] = DECLINE_TOKEN;
      } else {
        tokens[cast.other] = token;
        tokens[declined === 'you-declined' ? cast.you : cast.third] = DECLINE_TOKEN;
      }
      runs = '-2';
      break;
    case 'agreed':
    default:
      tokens[cast.other] = token;
      tokens[cast.you] = token;
      tokens[cast.third] = token;
      // The run-out form the builder already produced for this count.
      runs = String(frame.game.runs ?? '');
      break;
  }

  const game: Record<string, any> = {
    ...frame.game,
    runs,
    firstAllInPlayer: initiator,
    gameNo: demoVoteHand(hand),
    // Nobody is on a betting turn while the vote window is open.
    move: '0',
    time: startedAt,
    lastmove: startedAt,
    multiRunVoteSecs: DEMO_VOTE_SECS
  };
  for (const seat of [cast.you, cast.other, cast.third]) {
    if (tokens[seat]) game[`p${seat}action`] = tokens[seat];
  }

  return { ...frame, game };
};

/**
 * Where a demo tap takes the table: the answer a live table would broadcast.
 * `runs` is the count tapped (1 for RUN IT 1X). Null when the tap changes
 * nothing (no decision is yours in this step).
 */
export const stepAfterVote = (
  step: VoteStep,
  runs: number
): { step: VoteStep; offer?: VoteOffer; declined?: DeclineCase } | null => {
  if (step === 'you-choose') {
    if (runs === 1) return { step: 'declined', declined: 'you-picked-1x' };
    return runs === 2 || runs === 3 ? { step: 'you-offered', offer: runs } : null;
  }
  if (step === 'they-offered') {
    return runs === 1 ? { step: 'declined', declined: 'you-declined' } : { step: 'you-agreed' };
  }
  return null;
};
